import { Button } from '@design-system/button';
import { Link } from '@design-system/link';
import classNames from 'classnames';
import React, { FC } from 'react';

import { useDropdown } from '../../hooks/useDropdown';
import { getLinkIcon } from '../../utils/getLinkIcon';
import { getLinkSize } from '../../utils/getLinkSize';
import { isActive } from '../../utils/isActive';
import { SubNavigation } from '../sub-navigation';
import { NavigationBasicItemProps } from './models';

export const NavigationBasicItem: FC<NavigationBasicItemProps> = ({
  className,
  linkNavElement,
  activeUrlPath,
  href = '',
  ariaLabel,
  isMobileView,
  label,
  subItems,
  subMenuLabel,
  openIcon,
  closeIcon,
  expandTitle,
  onClickNavItem,
  handleMobileNavigationItemClick,
}) => {
  const LinkElement = linkNavElement || Link;
  const {
    handleDesktopBlur,
    handleDesktopOutsideBlur,
    handleFocus,
    handleMenuItemClick,
    handleKeyPress,
    isOpen,
    handleMouseOver,
  } = useDropdown();

  const hasSubItems = Boolean(subItems?.length);
  const onMouseOverDesktop =
    hasSubItems && !isMobileView ? handleMouseOver : undefined;

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    onClickNavItem?.(label);
    handleMenuItemClick(e, hasSubItems, handleMobileNavigationItemClick);
  };

  return (
    <li
      className={classNames('cds-global-header__navigation-item', className, {
        'cds-global-header__navigation-item--has-children': hasSubItems,
        active: href && isActive(activeUrlPath, href, subItems || []),
      })}
      onBlur={handleDesktopOutsideBlur}
      onMouseLeave={handleDesktopBlur}
    >
      {href ? (
        <>
          <LinkElement
            className={classNames('cds-global-header__navigation-link', {
              active: isActive(
                decodeURIComponent(activeUrlPath),
                href,
                subItems || []
              ),
              'has-submenu': hasSubItems,
            })}
            onClick={handleClick}
            aria-current={decodeURIComponent(activeUrlPath) === href}
            onMouseOver={onMouseOverDesktop}
            href={href}
            variant="nav"
            size={getLinkSize(isMobileView)}
            aria-label={ariaLabel}
          >
            {label}
          </LinkElement>
          {hasSubItems && (
            <Button
              variant={isMobileView ? 'primary-icon' : 'nav'}
              onClick={handleClick}
              onKeyDown={handleKeyPress}
              onMouseOver={onMouseOverDesktop}
              size="small"
              className="cds-global-header__navigation-arrow"
              aria-expanded={isOpen}
              iconSuffix={getLinkIcon(openIcon, closeIcon, isOpen)}
            >
              {`${expandTitle} ${label}`}
            </Button>
          )}
        </>
      ) : (
        <Button
          className={classNames('cds-global-header__navigation-btn', {
            'has-submenu': hasSubItems,
          })}
          variant="nav"
          onClick={handleClick}
          onKeyDown={handleKeyPress}
          onMouseOver={onMouseOverDesktop}
          size={getLinkSize(isMobileView)}
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          iconSuffix={
            hasSubItems ? getLinkIcon(openIcon, closeIcon, isOpen) : undefined
          }
        >
          {label}
        </Button>
      )}
      {hasSubItems && (
        <SubNavigation
          subItems={subItems!}
          activeUrlPath={decodeURIComponent(activeUrlPath)}
          subMenuLabel={subMenuLabel}
          linkNavElement={linkNavElement}
          handleFocus={handleFocus}
          handleBlur={handleDesktopBlur}
          isMobileView={isMobileView}
          href={href}
          ariaLabel={ariaLabel}
          handleMobileNavigationItemClick={handleMobileNavigationItemClick}
          onClickSubnavItem={onClickNavItem}
        />
      )}
    </li>
  );
};
